import { set } from "lodash";
import { useEffect, useRef, useMemo } from "react";

export default function Keyboard({
  options = [[]],
  selected = [1, 0],
  setSelected,
  keyEsc,
  keyDownCheck,
  isEnabled = true,
  loop = true,
}) {
  const tabOptions = useMemo(() => {
    return options.flat();
  }, [options]);

  const isValidSelected = Array.isArray(selected) && selected.length === 2;
  const enterLockRef = useRef(false); // 🔐 用來防止連點

  const isValidElement = (id) => {
    const el = document.getElementById(id);
    if (!el) return false;

    const style = window.getComputedStyle(el);
    const isVisiblyHidden =
      style.display === "none" || style.visibility === "hidden";

    const isCheckboxDisabled =
      el.getAttribute("aria-disabled") === "true" ||
      el.getAttribute("tabindex") === "-1";

    return !el.disabled && !isVisiblyHidden && !isCheckboxDisabled;
  };

  // Finds the next valid item in a 2D array, searching linearly.
  const findNextValidFocus = (startRow, startCol) => {
    for (let r = startRow; r < options.length; r++) {
      const c_start = r === startRow ? startCol : 0;
      for (let c = c_start; c < options[r].length; c++) {
        if (isValidElement(options[r][c])) return [r, c];
      }
    }
    if (loop) {
      for (let r = 0; r < startRow; r++) {
        for (let c = 0; c < options[r].length; c++) {
          if (isValidElement(options[r][c])) return [r, c];
        }
      }
      for (let c = 0; c < startCol; c++) {
        if (isValidElement(options[startRow][c])) return [startRow, c];
      }
    }
    return null;
  };

  // Finds the previous valid item in a 2D array, searching linearly.
  const findPreviousValidFocus = (startRow, startCol) => {
    for (let r = startRow; r >= 0; r--) {
      const c_start = r === startRow ? startCol : options[r].length - 1;
      for (let c = c_start; c >= 0; c--) {
        if (isValidElement(options[r][c])) return [r, c];
      }
    }
    if (loop) {
      for (let r = options.length - 1; r > startRow; r--) {
        for (let c = options[r].length - 1; c >= 0; c--) {
          if (isValidElement(options[r][c])) return [r, c];
        }
      }
      for (let c = options[startRow].length - 1; c > startCol; c--) {
        if (isValidElement(options[startRow][c])) return [startRow, c];
      }
    }
    return null;
  };

  // Finds the next/previous valid item in a flattened list (for Tab key).
  const findNextValidCoordsInFlatList = (startIndex, direction) => {
    const len = tabOptions.length;
    if (len === 0) return null;

    for (let i = 1; i <= len; i++) {
      let nextIndex = startIndex + direction * i;

      if (loop) {
        nextIndex = ((nextIndex % len) + len) % len;
      } else {
        if (nextIndex < 0 || nextIndex >= len) {
          continue;
        }
      }

      const id = tabOptions[nextIndex];
      if (isValidElement(id)) {
        // Found a valid element. Calculate its [r, c] from nextIndex.
        let count = 0;
        for (let r = 0; r < options.length; r++) {
          for (let c = 0; c < options[r].length; c++) {
            if (count === nextIndex) {
              return [r, c];
            }
            count++;
          }
        }
      }
    }

    return null;
  };

  function keyCheck(event, row, col) {
    const maxRow = options.length - 1;
    const maxCol = options[row]?.length - 1 || 0;
    let nextValid;

    switch (event.code) {
      case "ArrowUp":
        row = loop
          ? (row - 1 + options.length) % options.length
          : Math.max(0, row - 1);
        col = options[row].length - 1;
        nextValid = findPreviousValidFocus(row, col);
        break;
      case "ArrowDown":
        row = loop ? (row + 1) % options.length : Math.min(maxRow, row + 1);
        col = 0;
        nextValid = findNextValidFocus(row, col);
        break;
      case "ArrowLeft":
        col = loop
          ? (col - 1 + options[row].length) % options[row].length
          : Math.max(0, col - 1);
        nextValid = findPreviousValidFocus(row, col);
        break;
      case "ArrowRight":
        col = loop
          ? (col + 1) % options[row].length
          : Math.min(maxCol, col + 1);
        nextValid = findNextValidFocus(row, col);
        break;
      case "Tab": {
        // Calculate current index in the flat list based on [row, col]
        // This is more robust than indexOf() if there are duplicate item IDs.
        let currentIndex = 0;
        for (let i = 0; i < row; i++) {
          currentIndex += options[i].length;
        }
        currentIndex += col;

        const direction = event.shiftKey ? -1 : 1;

        const nextCoords = findNextValidCoordsInFlatList(
          currentIndex,
          direction
        );
        if (nextCoords) {
          event.preventDefault();
          setSelected(nextCoords);
        } else if (!loop) {
          if (keyEsc) keyEsc();
        } else {
          event.preventDefault();
        }
        return;
      }
      case "Enter":
        keyEnter(event);
        return;
      case "Escape":
        if (keyEsc) {
          keyEsc(event)
        } 
        return;
      default:
        console.warn(`未處理的鍵盤事件：${event.code}`);
        return;
    }
    if (nextValid) setSelected(nextValid);
  }

  useEffect(() => {
    if (!isValidSelected || !isEnabled) return;
    const handleKeyDown = (event) => {
        if (keyDownCheck && keyDownCheck(event.code)) return;
      let [row, col] = selected;

      keyCheck(event, row, col);
    };

    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [selected, options, loop, keyDownCheck, isEnabled]);

  useEffect(() => {
    if (!isValidSelected) return;
    const handleMouseDown = (event) => {
      // 優先從 data-id 找（按鈕、checkbox 等元素）
      const dataEl = event.target.closest("[data-id]");
      const id = dataEl
        ? dataEl.getAttribute("data-id")
        : event.target.id || null;

      if (!id) return;

      for (let i = 0; i < options.length; i++) {
        for (let j = 0; j < options[i].length; j++) {
          if (options[i][j] === id && isValidElement(id)) {
            setSelected([i, j]);
            console.log(`🖱️ 滑鼠點擊 id="${id}", row=${i}, col=${j}`);
            return;
          }
        }
      }
    };

    window.addEventListener("mousedown", handleMouseDown, true);

    return () => {
      window.removeEventListener("mousedown", handleMouseDown, true);
    };
  }, [options, isValidSelected]);

  const isEnabledRef = useRef(isEnabled);
  useEffect(() => {
    isEnabledRef.current = isEnabled;
  }, [isEnabled]);

  useEffect(() => {
    if (!isValidSelected || !isEnabledRef.current) return;

    const [row, col] = selected;
    const id = options?.[row]?.[col];
    if (!id) {
      console.warn("無效的 selected id：", row, col);
      setSelected([0, 0]);
      return;
    }

    const el = document.getElementById(id);
    if (!el) {
      console.warn("找不到 DOM 元素 id=", id);
      return;
    }
    if (el && typeof el.focus === "function") {
      el.focus();
      console.log(
        `🔍 Focus 元件：id="${id}", row=${row}, col=${col}, placeholder="${el.placeholder}"`
      );
    }
    console.log(id, selected, options, isEnabled);
  }, [selected, options]);

  function keyEnter(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (enterLockRef.current) return;
    enterLockRef.current = true;
    const [row, col] = selected;
    const id = options?.[row]?.[col];
    const el = document.getElementById(id);

    console.log(`🔑 Enter 鍵被按下, select: ${selected}`);

    if (el && typeof el.click === "function") {
      // 延遲觸發 click，避免在事件冒泡中造成多重影響
      setTimeout(() => {
        el.click();

        console.log(
          `🖱️ Click 元件：id="${id}", row=${row}, col=${col}, placeholder="${el.placeholder}"`
        );
        // 再延遲解鎖，確保 dialog 建立完畢
        setTimeout(() => {
          enterLockRef.current = false;
          console.log("🔓 Enter 解鎖");
        }, 300); // 根據動畫時間或 dialog 建立時間調整
      }, 0);
    } else {
      enterLockRef.current = false;
    }
  }

  return null;
}
